// kfe-app-shell.js — top-level shell + router host.
//
// Mirrors AppNavigator from the Unity port. Provides:
//   • Header with logo, context-dependent title, desktop section top-nav,
//     and the overflow menu (hamburger).
//   • Outlet for the routed screen (<main>).
//   • A bottom tab bar (<kfe-tab-bar>) for the two top-level sections, shown
//     ≤1024px and replaced by the header top-nav ≥1024px.
//   • A persistent "now playing" bar (<kfe-now-playing>, Brief 091) — chrome
//     that survives route changes and shows the active Global-Playlist track
//     anywhere in the app; collapsed when nothing is playing.
//   • Footer with version.
//
// Two-section navigation (Brief 089, design-doc §3-§4): Learning | Global
// Playlist. iOS is mobile-only with a bottom tab bar (TabBarViewController.m);
// the desktop top-nav is a deliberate web-platform addition (design-doc §2.1
// "desktop is first-class") — not an iOS mirror, since iOS has no desktop
// mode.
//
// Header title is set per-route by the mounted screen via a
// `kfe:set-header-title` CustomEvent that bubbles + composes through the
// shadow boundary. Each screen's `onConnected` is the right place to
// dispatch — that way back/forward navigation re-applies the title.
//
// RE-MOUNT DISCIPLINE (the #1 risk here): the shell must NOT re-render itself
// on route changes, because that replaces the <main> outlet and would unmount
// + remount every routed screen on every navigation. The back chevron, the
// bottom tab bar's active tab, and the top-nav's active link are all updated
// IN PLACE on `kfe:route-changed` (see updatePath below) — never via a full
// render(). The shell only re-renders when i18n strings arrive, and that path
// explicitly re-mounts the router afterwards.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import { VERSION } from "../version.js";
import { sectionForPath } from "../ui/kfe-tab-bar.js";
import { gatingEvents } from "../data/gating.js";
import { isDevMode } from "../core/dev-mode.js";
import { initialiseSync, teardownSync, flush } from "../data/sync-engine.js";
import { isSignedIn, signOut, markSessionExpiredNotice } from "../data/auth-store.js";
import { onSessionInvalid } from "../data/crm-client.js";
import { KFE_LOGO_SRC } from "../ui/icons/kfe-logo.js";

// Production route table — every route present in every build.
/** @type {Array<{path: string, tag: string, props?: (p: any) => any}>} */
const BASE_ROUTES = [
  { path: "/",                        tag: "kfe-pathway",         props: () => ({ level: "stage" }) },
  { path: "/stage/:stageId",          tag: "kfe-pathway",         props: (p) => ({ level: "lesson", stageId: p.stageId }) },
  // Brief 168: content-folder drilldown inside a flat (menu) pathway —
  // iOS pushes a child TableListViewController; the web re-enters
  // <kfe-pathway-list> with the folder id (children from folders.json).
  { path: "/folder/:folderId",        tag: "kfe-pathway-list",    props: (p) => ({ folderId: p.folderId }) },
  { path: "/lesson/:lessonId",        tag: "kfe-lesson-detail",   props: (p) => ({ lessonId: p.lessonId }) },
  { path: "/lesson/:lessonId/report", tag: "kfe-lesson-report",   props: (p) => ({ lessonId: p.lessonId }) },
  { path: "/exercise/:itemId",        tag: "kfe-exercise-host",   props: (p) => ({ itemId: p.itemId }) },
  { path: "/playlist",                tag: "kfe-global-playlist" },
  { path: "/playlist/:trackId",       tag: "kfe-global-playlist", props: (p) => ({ trackId: p.trackId }) },
  { path: "/settings",                tag: "kfe-settings" },
  { path: "/settings/pathway",        tag: "kfe-pathway-switcher" },
  { path: "/login",                   tag: "kfe-login" },
  { path: "/personal-promise",        tag: "kfe-personal-promise" },
];

// Dev-only routes — appended to the table ONLY when per-device Developer mode is
// active (Brief 175 HAAI, Brief 181 SR, Brief 181c T/F, Brief 181d MC, Brief 181e
// meter). Each <kfe-dev-*-walker> module is dynamically imported (and
// self-registers) before router.start, so it is a separate chunk production never
// loads and these routes are genuinely absent from the production route table.
/** @type {Array<{path: string, tag: string, props?: (p: any) => any}>} */
const DEV_ROUTES = [
  { path: "/dev/haai-walker", tag: "kfe-dev-haai-walker" },
  { path: "/dev/sr-walker", tag: "kfe-dev-sr-walker" },
  { path: "/dev/tf-walker", tag: "kfe-dev-tf-walker" },
  { path: "/dev/mc-walker", tag: "kfe-dev-mc-walker" },
  { path: "/dev/meter-walker", tag: "kfe-dev-meter-walker" },
];

/**
 * The route table for a given Developer-mode state. Exported pure so the
 * route-registration contract (dev route present iff devMode) is unit-testable
 * without driving the live router.
 * @param {boolean} devMode
 * @returns {Array<{path: string, tag: string, props?: (p: any) => any}>}
 */
export function buildRoutes(devMode) {
  return devMode ? [...BASE_ROUTES, ...DEV_ROUTES] : [...BASE_ROUTES];
}

// Route guard (Brief 188, revised 2026-11-02). `/login` and `/forgot-password`
// (Brief 148's screen) are the only public routes. EVERY other route — `/`
// included — bounces a signed-out, non-dev-mode visitor to /login with a
// `?return=` back-link.
//
// `/` WAS public: Brief 188 reasoned that the pathway home needs no session of
// its own, only the auth-gated features behind it. **Chris reversed that on
// 2026-11-02**: a signed-out visitor must see the login screen first and never
// the pathway, because iOS shows login / activation before any course content
// (Rule #9). Signing in with no `?return=` still lands on `/`, so the
// after-login destination is unchanged — only what an unauthenticated visitor
// sees first has changed.
//
// Dev mode is untouched: guardPath() short-circuits on devMode before it looks
// at this set, which is what keeps `?dev=1` and the synthetic-learner harness
// working exactly as before.
const PUBLIC_PATHS = new Set(["/login", "/forgot-password"]);

// The routes that render chromeless over the splash artwork (Brief 121).
// Same two routes as PUBLIC_PATHS today, but kept separate because they
// answer different questions — "may a signed-out visitor see this?" versus
// "does this screen carry the app chrome?" — and a future public-but-chromed
// route (a marketing or help page, say) must not silently lose its header.
const AUTH_SCREEN_PATHS = new Set(["/login", "/forgot-password"]);

/**
 * Pure guard decision, registered with router.setGuard() in onConnected.
 * Exported (mirrors buildRoutes above) so the redirect logic is
 * unit-testable without driving the live router.
 * @param {string} path
 * @param {{ signedIn: boolean, devMode: boolean }} state
 * @returns {string | null}
 */
export function guardPath(path, { signedIn, devMode }) {
  if (signedIn || devMode) return null;
  if (PUBLIC_PATHS.has(path)) return null;
  // Brief 121 criterion 3: a bare "/" gets a clean `/login`, with no
  // `?return=`. Behaviour is identical either way — signing in with no
  // `?return=` already defaults to "/" (kfe-login._returnPath) — so this is
  // purely the URL the learner sees on the very first screen of the app.
  // `/login?return=%2F` is noise on what is, for a signed-out visitor,
  // effectively the front door. Every OTHER path keeps its back-link
  // (criterion 6 / Brief 188): a deep link to /exercise/123 must come back.
  if (path === "/") return "/login";
  return `/login?return=${encodeURIComponent(path)}`;
}

export class KFEAppShell extends KFEComponent {
  static styles = `
    /* header / main / now-playing / tab-bar / footer. The now-playing + tab-bar
       rows are auto and collapse to 0 when their components are display:none —
       <kfe-now-playing> when nothing is playing, <kfe-tab-bar> at the desktop
       breakpoint. */
    :host { display: grid; grid-template-rows: auto 1fr auto auto auto auto; height: 100%; }
    header {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: var(--kfe-bg-header);
      border-bottom: 1px solid var(--kfe-border);
    }
    header h1 {
      margin: 0; font-size: var(--kfe-h1); color: var(--kfe-fg);
      flex: 1; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    /* KFE standalone mark in the header (iOS canon, Rule #9 — corrected
       2026-06-18 by Brief 164 Sub-fix 1; was the brand lockup, which was the
       wrong iOS asset for the in-app navigation chrome — see kfe-logo.js
       docblock). Leftmost element in the shared header — present on every
       route per Brief 099 §1. Height-constrained to the header bar; the mark
       is square (1:1), so width matches height. */
    .logo {
      display: inline-flex; align-items: center;
      flex: 0 0 auto; margin-right: 0.5rem;
    }
    .logo img { height: 26px; width: 26px; display: block; }
    /* Brief 166 (2026-06-20): hit-target widened to iOS HIG ≥44×44pt. Chris's
       Safari Web Inspector measurement of $0.getBoundingClientRect() returned
       width:11.65 × height:28 — well below the 44×44 minimum. Touch precision
       on a finger is ~30-40px, so most taps physically miss the <button>
       element even when they appear to land on the ‹ glyph (Brief 166's
       multi-tap symptom — diagnosed conclusively as hypothesis 1, hit target
       below HIG). The flex centring keeps the visual chevron at its original
       position inside the now-44px box; layout shift is only the title moving
       ~32px to the right, which the header has plenty of room for. */
    /* Brief 198 item 9: the chevron is ORANGE on iOS, not white — see
       docs/198-reference/ios-lesson-map.jpg, where it sits in the nav bar in
       the same tint as the Learning tab. iOS shows no chevron at the root of
       a section, which the hidden rule in render() already mirrors. */
    button.back {
      background: transparent; border: 0; color: var(--kfe-accent);
      font-size: 1.75rem; line-height: 1; padding: 0; margin-right: 0.25rem;
      cursor: pointer;
      min-width: 44px; min-height: 44px;
      display: inline-flex; align-items: center; justify-content: center;
    }
    button.back[hidden] { display: none; }

    /* Desktop section top-nav — hidden ≤1024px, where the bottom tab bar
       takes over. 1024px === --kfe-bp-desktop (tokens.css); keep in sync. */
    nav.top-nav { display: none; }
    @media (min-width: 1024px) {
      nav.top-nav { display: flex; gap: 1.25rem; margin-left: 0.75rem; }
      header h1 { text-align: center; }
    }
    nav.top-nav a {
      color: var(--kfe-fg); text-decoration: none;
      padding: 0.15rem 0; white-space: nowrap;
      border-bottom: 2px solid transparent;
    }
    nav.top-nav a[aria-current="page"] {
      color: var(--kfe-accent); border-bottom-color: var(--kfe-accent);
    }

    /* Brief 198: the page gutter is a token so the squiggle can bleed back
       out of it — iOS hands the gating-line view the FULL screen width and
       bakes its own 35 pt inset into the drawing box
       (StagesViewController.m:451, :457), so a squiggle inset by the page
       gutter on top renders ~9% small. */
    /* Brief 198 item 10 (Chris, 1 Nov 2026): on a desktop browser the app
       column used to sit against the left edge with empty space to the right.
       Growing the inline padding once the viewport is wider than the column
       centres EVERY screen — it is one shell-level rule, not a per-screen one —
       and leaves the phone-width layout byte-identical, because below
       --kfe-app-max-width the max() always picks the page gutter. */
    main {
      overflow: auto;
      padding-block: var(--kfe-page-pad, 1rem);
      padding-inline: max(
        var(--kfe-page-pad, 1rem),
        calc((100% - var(--kfe-app-max-width, 480px)) / 2)
      );
      background: var(--kfe-bg); color: var(--kfe-fg);
    }

    /* Pre-auth screens are chromeless (Brief 121 criterion 5, Rule #9).
       iOS's login / activation screens are full-screen over LoginBackground —
       no nav bar, no tab bar — and so is the launch screen that precedes
       them. Leaving the app chrome up would put a dark header and a tab bar
       on top of the splash artwork the instant the splash came down, which is
       precisely the "layout shift between splash and login" the brief rules
       out. Set by _updatePath on every route change.

       Brief 188 originally rendered /login inside the full chrome; this
       supersedes that for the two pre-auth routes only. */
    :host([data-auth-screen]) header,
    :host([data-auth-screen]) footer,
    :host([data-auth-screen]) kfe-tab-bar,
    :host([data-auth-screen]) kfe-now-playing,
    /* Brief 199's completion-write alert is chrome too, so it goes with the
       rest of it on a pre-auth screen (merge of 121 + 199, 2026-11-03). It is
       display:none unless a write failed, but a failure followed by Brief 194's
       deferred sign-out would otherwise leave it sitting on the login artwork —
       the layout shift 121 criterion 5 rules out. The learner meets the message
       again on the exercise, where it is actionable. */
    :host([data-auth-screen]) .write-alert { display: none; }
    :host([data-auth-screen]) main {
      background: transparent;
      display: grid;
      place-items: center;
      /* Chromeless means no page gutter either. Brief 188's form was an
         in-flow column that wanted the padding; the xib-faithful login
         (Brief 121 amendment, 2026-11-03) is a full-viewport fixed layer,
         so the gutter only added 2rem of scroll below a screen that must
         not scroll at all. */
      padding: 0;
      /* With the header/footer/tab-bar gone the shell host is auto-height, so
         <main> would shrink to the form's own 414px and strand it at the top
         of the artwork. iOS centres the login form on the backdrop; this is
         what makes place-items actually have room to do that. dvh first so
         mobile Safari's collapsing URL bar doesn't crop it, with vh behind
         for anything that lacks dvh. */
      min-height: 100vh;
      min-height: 100dvh;
    }
    footer {
      padding: 0.5rem 1rem; font-size: 0.75rem; color: var(--kfe-muted);
      border-top: 1px solid var(--kfe-border); text-align: center;
    }
    /* Brief 199, criterion 4 (Chris's ruling, 2 Nov 2026): when a completion
       write has failed every retry, the learner is TOLD rather than left with a
       shut gate and no explanation. Shell-level rather than per-screen because
       every exercise type can hit it, including the ones with no completion
       screen of their own (HTML, music track, audio). Styled as the app's own
       inline problem message (cf. the video.error / login.errorGeneric rows) —
       not a floating toast, because the learner has to act on it. */
    .write-alert {
      display: none;
      padding: 0.6rem 1rem; font-size: 0.9rem; line-height: 1.35;
      background: var(--kfe-surface); color: var(--kfe-fg);
      border-top: 2px solid var(--kfe-error, #e57373);
    }
    .write-alert[data-shown="true"] { display: flex; align-items: center; gap: 0.75rem; }
    .write-alert p { margin: 0; flex: 1; }
    .write-alert button {
      flex: 0 0 auto; min-width: 44px; min-height: 44px;
      background: transparent; border: 0; color: var(--kfe-muted);
      font-size: 1.25rem; line-height: 1; cursor: pointer;
    }
  `;

  /** @type {string} */
  _title = "";
  /** @type {string} */
  _path = "/";
  /** Brief 199: the item whose completion could not be saved, or null. */
  /** @type {number|null} */
  _writeFailedItemId = null;

  onConnected() {
    // Route guard (Brief 188). Registered before router.start() below so the
    // very first render is guarded too, not just subsequent navigations.
    // guardPath() re-reads isSignedIn()/isDevMode() fresh on every call (it's
    // a closure, not a captured snapshot), so a mid-session sign-out/sign-in
    // takes effect on the next navigation with no re-registration needed.
    router.setGuard((path) => guardPath(path, { signedIn: isSignedIn(), devMode: isDevMode() }));

    // A displaced/expired session (crm-client's onSessionInvalid seam, or a
    // plain signOut()) dispatches kfe:auth-changed. If there's no session
    // left, route to /login — the guard above would catch the NEXT
    // navigation anyway, but a screen already mounted on a protected route
    // needs an explicit kick to leave it now.
    //
    // Brief 025 wires the sync engine to the same signal: signed-in starts the
    // heartbeat + pull-on-login, signed-out stops the heartbeat (and leaves the
    // outbox alone, so queued rows ship after the next login). `initialiseSync`
    // is idempotent, so a duplicate event can't double-arm anything.
    const onAuthChanged = () => {
      if (!isSignedIn()) {
        teardownSync();
        router.navigate("/login");
        return;
      }
      void initialiseSync().catch((err) => console.warn("[app-shell] initialiseSync failed", err));
    };
    document.addEventListener("kfe:auth-changed", onAuthChanged);
    this.addCleanup(() => document.removeEventListener("kfe:auth-changed", onAuthChanged));

    // Brief 025 hotfix 6 (2026-09-15 live smoke): wire crm-client's
    // onSessionInvalid seam to the local sign-out path. Without this
    // subscription every 401 (JWT expiry, cross-client displacement) fires
    // notifySessionInvalid() into an empty listener list — the token stays
    // in localStorage, the outbox keeps retrying forever with the dead
    // token, and the learner sees a functional-looking app that silently
    // loses every progress write. signOut() clears the token + dispatches
    // kfe:auth-changed, which the onAuthChanged handler above then routes
    // to /login. Idempotent (multiple 401s in flight → multiple signOut()
    // calls → clearToken is a no-op after the first).
    //
    // Brief 194 splits the two reasons. `"displaced"` (409/91049) keeps the
    // immediate sign-out above: the server has already handed the session to
    // another device, so there is nothing left to finish. `"expired"`
    // (401/20000 whose refresh failed) defers to _deferSignOutForExpiry()
    // below, so the learner's current item is allowed to finish and save first.
    const unsubSessionInvalid = onSessionInvalid((reason) => {
      if (!isSignedIn()) return;
      if (reason === "expired") { this._deferSignOutForExpiry(); return; }
      signOut();
    });
    this.addCleanup(unsubSessionInvalid);
    this.addCleanup(() => this._cancelExpiryDeferral());

    // A reload on an already-signed-in session never fires kfe:auth-changed, so
    // start the engine here too. Same idempotent call — whichever path runs
    // first wins and the other is a no-op.
    if (isSignedIn()) {
      void initialiseSync().catch((err) => console.warn("[app-shell] initialiseSync failed", err));
    }

    // Brief 025: `signOutFull` dispatches this just before it drops the
    // credential. One best-effort flush, then tear down. NOT awaited — Brief
    // 191's rule is that sign-out never blocks on a server call.
    const onSyncTeardown = () => {
      void flush({ reason: "breakpoint-lesson" })
        .catch(() => {})
        .finally(() => teardownSync());
    };
    document.addEventListener("kfe:sync-teardown", onSyncTeardown);
    this.addCleanup(() => document.removeEventListener("kfe:sync-teardown", onSyncTeardown));

    document.addEventListener("kfe:ready", async () => {
      const devMode = isDevMode();
      // Load + self-register the dev-only walker BEFORE router.start, so its
      // route tag resolves. The dynamic import keeps the module out of the
      // production bundle path (Brief 175) — production never enters this branch.
      if (devMode) {
        try { await import("./kfe-dev-haai-walker.js"); }
        catch (e) { console.warn("[app-shell] dev HAAI walker failed to load", e); }
        try { await import("./kfe-dev-sr-walker.js"); }
        catch (e) { console.warn("[app-shell] dev SR walker failed to load", e); }
        try { await import("./kfe-dev-tf-walker.js"); }
        catch (e) { console.warn("[app-shell] dev T/F walker failed to load", e); }
        try { await import("./kfe-dev-mc-walker.js"); }
        catch (e) { console.warn("[app-shell] dev MC walker failed to load", e); }
        try { await import("./kfe-dev-meter-walker.js"); }
        catch (e) { console.warn("[app-shell] dev meter walker failed to load", e); }
      }
      router.start(
        buildRoutes(devMode),
        /** @type {HTMLElement} */ (this.shadowRoot?.querySelector("main")),
      );

      // Brief 121: the app is ready — the session has been read from storage,
      // i18n has resolved, and router.start() above has just mounted the
      // first screen (pathway if signed in, /login if not, dev routes under
      // ?dev=1). Only NOW does the splash come down, so the learner never
      // sees a flash of the pathway behind the login guard, or of empty
      // chrome before the first screen exists.
      //
      // Gated on kfe:ready and NOT on the media manifest, despite the brief's
      // goal line mentioning it: Brief 122 deliberately moved that ~1 MB
      // fetch off the critical path (app.js dispatches kfe:ready before it),
      // and re-gating first paint on it here would hand back the second that
      // brief bought. kfe:ready IS this app's "ready".
      //
      // dismiss() owns the 600 ms minimum, measured from navigation start —
      // see KFESplash.MIN_DISPLAY_MS. Not awaited: the splash is an overlay,
      // so holding it up must never hold up the app underneath it.
      void this._dismissSplash();
    }, { once: true });

    // Routed screens dispatch `kfe:set-header-title` from their onConnected.
    // Composed: true lets the event cross the shadow boundary and reach us
    // here on the host. Update the <h1> in place rather than re-rendering
    // so we don't blow away the routed <main> outlet.
    const onTitle = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent<{title?: string}>} */ (e).detail;
      const title = detail?.title ?? t("app.title");
      this._title = title;
      const h1 = /** @type {HTMLElement | null} */ (this.shadowRoot?.querySelector("header h1"));
      if (h1) h1.textContent = title;
    };
    this.addEventListener("kfe:set-header-title", onTitle);
    this.addCleanup(() => this.removeEventListener("kfe:set-header-title", onTitle));

    // The shell renders synchronously before setLanguage resolves, so the
    // header initially shows raw i18n keys. Re-render once translations
    // arrive. Note: re-rendering blows away the <main> outlet's children;
    // the router will re-mount the current route on the next render() pass
    // because the shell renders an empty <main>, so we re-trigger the
    // router after this re-render.
    const onI18n = () => {
      this._scheduleRender();
      // Brief 199: a re-render builds a fresh (empty) alert row, so re-paint it
      // if a completion failure is still unacknowledged.
      queueMicrotask(() => this._applyWriteAlert());
      // Wait a microtask for the new <main> to exist, then re-mount.
      queueMicrotask(() => {
        const main = /** @type {HTMLElement|null} */ (this.shadowRoot?.querySelector("main"));
        if (main) router.remount(main);
      });
    };
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // In-place nav-state updates on every route change. Updates: back-chevron
    // visibility (shown except on a section root), the bottom tab bar's active
    // tab (via its `path` prop), and the desktop top-nav's active link. NEVER
    // re-renders the shell — that would unmount the routed screen.
    window.addEventListener("popstate", this._updatePath);
    document.addEventListener("kfe:route-changed", this._updatePath);
    this.addCleanup(() => window.removeEventListener("popstate", this._updatePath));
    this.addCleanup(() => document.removeEventListener("kfe:route-changed", this._updatePath));
    queueMicrotask(this._updatePath);

    // Auto-show the Lesson Report when a lesson completes (Brief 097 §8). The
    // gating module fires `lesson_completed` AT MOST once per lesson (Brief 095
    // idempotency — re-mastering does NOT re-pop), so we don't dedupe here. We
    // do guard against racing the just-finished exercise's own navigation: the
    // event fires synchronously inside markItemCompleted, mid-completion, so we
    // defer the navigate to a microtask to let that settle (Brief 097 gotcha
    // "auto-show race"). Idempotent: no-op if the report is already showing.
    gatingEvents.addEventListener("lesson_completed", this._onLessonCompleted);
    this.addCleanup(() => gatingEvents.removeEventListener("lesson_completed", this._onLessonCompleted));

    // Brief 199, criterion 4: a completion write that failed every retry.
    // Updated in place (never a re-render — that would unmount the routed
    // exercise the learner is still standing in), exactly as the header title
    // handler above does.
    document.addEventListener("kfe:completion-write-failed", this._onCompletionWriteFailed);
    this.addCleanup(() => document.removeEventListener("kfe:completion-write-failed", this._onCompletionWriteFailed));
  }

  _onCompletionWriteFailed = (/** @type {Event} */ e) => {
    const detail = /** @type {CustomEvent<{itemId?: number}>} */ (e).detail;
    this._writeFailedItemId = detail?.itemId ?? null;
    this._applyWriteAlert();
  };

  _dismissWriteAlert = () => {
    this._writeFailedItemId = null;
    this._applyWriteAlert();
  };

  /** Paint (or clear) the completion-write failure message in place. */
  _applyWriteAlert() {
    const root = this.shadowRoot;
    if (!root) return;
    const box = /** @type {HTMLElement|null} */ (root.querySelector(".write-alert"));
    if (!box) return;
    const shown = this._writeFailedItemId != null;
    box.dataset.shown = shown ? "true" : "false";
    const p = box.querySelector("p");
    if (p) p.textContent = shown ? t("completion.writeFailed") : "";
  }

  /**
   * Take the boot splash down. Tolerates its absence: the component tests
   * mount a bare <kfe-app-shell> with no splash in the document, and a
   * second kfe:ready can never fire (the listener is `once`), but a shell
   * re-mount would re-register it.
   * @returns {Promise<void>}
   */
  async _dismissSplash() {
    const splash = /** @type {any} */ (document.querySelector("kfe-splash"));
    if (splash && typeof splash.dismiss === "function") {
      await splash.dismiss();
      return;
    }
    // No component (bundle still loading, or a test harness) — the static
    // first frame would otherwise sit over the app forever.
    document.getElementById("kfe-boot-splash")?.remove();
  }

  // ---- Brief 194: deferred sign-out on token expiry -----------------------
  //
  // An expired token must not cost the learner the item they are in the middle
  // of. The 401 has already failed its refresh by the time we hear about it, so
  // the session IS dead and nothing more will sync — but the local write path
  // is untouched: the exercise screen finishes, learner-store writes the play
  // to IndexedDB, and sync-engine's durable outbox holds the row until the next
  // login ships it. So we simply hold the route change.
  //
  // Released by whichever comes first: the current item completing
  // (`kfe:item-completed`, dispatched by learner-store AFTER the local write),
  // the learner navigating away, or the grace window below.

  /** @type {boolean} */
  _expiryPending = false;
  /** @type {any} */
  _expiryTimer = null;
  /** @type {any} */
  _expiryRecheckTimer = null;

  /**
   * Longest we will hold a known-dead session before signing out anyway. Set
   * above the longest single item in the corpus (an ~8-minute lesson video) so
   * a legitimately long item still finishes, but bounded — a learner who walks
   * away mid-item should not come back to an app that looks signed in.
   */
  static EXPIRY_GRACE_MS = 15 * 60_000;

  /** @returns {boolean} whether the learner is inside an exercise right now */
  _onExercisePath() {
    return window.location.pathname.startsWith("/exercise/");
  }

  /**
   * Hold the sign-out until the current item finishes. Idempotent — a burst of
   * 401s (heartbeat + push in the same second) arms this once.
   */
  _deferSignOutForExpiry() {
    if (this._expiryPending) return;

    if (!this._onExercisePath()) {
      // Not in an exercise — but possibly a few milliseconds away from one.
      // A heartbeat 401 resolves asynchronously, so it can land in the gap
      // between the learner tapping an item and router.navigate() pushing
      // /exercise/:id. Signing out in that gap looks to the learner exactly
      // like being thrown out "the instant the item opened" (2026-11-02 smoke).
      // Give an in-flight navigation one task to land, then decide again.
      if (this._expiryRecheckTimer !== null) return;
      this._expiryRecheckTimer = setTimeout(() => {
        this._expiryRecheckTimer = null;
        if (!this._connected || this._expiryPending) return;
        if (this._onExercisePath()) { this._deferSignOutForExpiry(); return; }
        this._signOutForExpiry();
      }, 0);
      return;
    }

    this._expiryPending = true;
    console.info("[app-shell] session expired mid-item — deferring sign-out until the item finishes");
    document.addEventListener("kfe:item-completed", this._releaseExpiryDeferral);
    document.addEventListener("kfe:route-changed", this._releaseExpiryDeferral);
    window.addEventListener("popstate", this._releaseExpiryDeferral);
    this._expiryTimer = setTimeout(this._releaseExpiryDeferral, KFEAppShell.EXPIRY_GRACE_MS);
  }

  _releaseExpiryDeferral = () => {
    if (!this._expiryPending) return;
    this._cancelExpiryDeferral();
    // A microtask so the completion write + the screen's own completion UI
    // settle before the route changes under them (same reasoning as the
    // lesson-report auto-show race below).
    queueMicrotask(() => this._signOutForExpiry());
  };

  /**
   * Drop the deferral's listeners and timer WITHOUT signing out. Runs from
   * disconnectedCallback via addCleanup (Common Pitfall #1/#2 — a timer that
   * outlives the component would route a detached shell).
   */
  _cancelExpiryDeferral() {
    this._expiryPending = false;
    if (this._expiryTimer !== null) { clearTimeout(this._expiryTimer); this._expiryTimer = null; }
    if (this._expiryRecheckTimer !== null) { clearTimeout(this._expiryRecheckTimer); this._expiryRecheckTimer = null; }
    document.removeEventListener("kfe:item-completed", this._releaseExpiryDeferral);
    document.removeEventListener("kfe:route-changed", this._releaseExpiryDeferral);
    window.removeEventListener("popstate", this._releaseExpiryDeferral);
  }

  /**
   * Sign out for expiry: arm the notice, then the LOCAL sign-out only.
   * Deliberately `signOut()`, not `signOutFull()` — the server has already
   * rejected this token, so DELETE /app/auth/logout would only earn a second
   * 401. `kfe:auth-changed` then routes to /login through onAuthChanged above,
   * and the outbox is left intact (teardownSync never clears it).
   */
  _signOutForExpiry() {
    if (!isSignedIn()) return;
    markSessionExpiredNotice();
    signOut();
  }

  _onLessonCompleted = (/** @type {Event} */ e) => {
    const detail = /** @type {CustomEvent<{lessonId?: string|number}>} */ (e).detail;
    const lessonId = detail?.lessonId;
    if (lessonId == null) return;
    const target = `/lesson/${lessonId}/report`;
    queueMicrotask(() => {
      if (window.location.pathname === target) return; // already showing
      router.navigate(target);
    });
  };

  _updatePath = () => {
    this._path = window.location.pathname;

    // Chromeless pre-auth routes (Brief 121 — see the :host([data-auth-screen])
    // rules). Toggled on the host, which is NOT in static props, so this does
    // not schedule a render: the shell must never re-render on a route change
    // (the RE-MOUNT DISCIPLINE note at the top of this file).
    this.toggleAttribute("data-auth-screen", AUTH_SCREEN_PATHS.has(this._path));

    const root = this.shadowRoot;
    if (!root) return;

    // Back chevron: hidden on a section root (Learning "/" or Global Playlist
    // "/playlist"), shown on detail screens.
    const btn = /** @type {HTMLButtonElement | null} */ (root.querySelector("button.back"));
    if (btn) btn.hidden = this._path === "/" || this._path === "/playlist";

    // Bottom tab bar active tab — set the prop, its reactive setter re-renders
    // the (sibling-of-<main>) tab bar in place.
    const tabBar = /** @type {any} */ (root.querySelector("kfe-tab-bar"));
    if (tabBar) tabBar.path = this._path;

    // Desktop top-nav active link.
    this._applyTopNavActive();
  };

  _applyTopNavActive() {
    const root = this.shadowRoot;
    if (!root) return;
    const section = sectionForPath(this._path);
    for (const a of root.querySelectorAll("nav.top-nav a")) {
      const el = /** @type {HTMLAnchorElement} */ (a);
      if (el.dataset.section === section) el.setAttribute("aria-current", "page");
      else el.removeAttribute("aria-current");
    }
  }

  // Brief 174 (2026-06-20): back chevron must NOT escape the SPA when the
  // user opened a non-root URL directly (deep link, bookmark, or hard
  // reload) — Chris hit a blocker walking Stage 2's squiggle where tapping
  // `<` popped Safari to the previously-loaded tab on a different site.
  //
  // The router (Brief 089, with the Brief 174 patch in router.js) marks
  // every pushState entry with `{ spa: true }`. On _onBack:
  //
  //   - `history.state?.spa === true` → the current entry was pushed by
  //     the SPA. Going back is safe — the previous entry is either an
  //     earlier SPA entry or the original page-load entry (which Safari
  //     handles correctly inside the same site).
  //   - `history.state?.spa` is falsy → we're sitting at the original
  //     page-load entry (no spa marker because navigate() never ran).
  //     `history.back()` would exit the SPA → instead fall back to
  //     `router.navigate("/")` so the user lands at the pathway home (the
  //     section root, where the chevron is correctly hidden by lines 198 + 243).
  //
  // This is the simplest reliable check that doesn't require a depth
  // counter or per-route parent map. `history.state` survives reloads of
  // the same URL on every modern browser (it's serialised into the
  // session-history entry), so a hard reload on a deep SPA URL keeps the
  // marker — the user can navigate back through their in-app history.
  _onBack = () => {
    if (history.state?.spa) {
      history.back();
    } else {
      router.navigate("/");
    }
  };

  render() {
    const title = this._title || t("app.title");
    const frag = html`
      <header>
        <a class="logo" href="/" aria-label="${t("app.title")}">
          <img src="${KFE_LOGO_SRC}" alt="${t("app.title")}" />
        </a>
        <button class="back" type="button" aria-label="${t("nav.back")}" on:click=${this._onBack}>‹</button>
        <nav class="top-nav">
          <a href="/" data-section="learning">${t("nav.learning")}</a>
          <a href="/playlist" data-section="playlist">${t("nav.globalPlaylist")}</a>
        </nav>
        <h1>${title}</h1>
        <kfe-app-menu></kfe-app-menu>
      </header>
      <main></main>
      <kfe-now-playing hidden></kfe-now-playing>
      <kfe-tab-bar .path=${this._path}></kfe-tab-bar>
      <div class="write-alert" role="alert" aria-live="assertive" data-shown="false">
        <p></p>
        <button type="button" aria-label="${t("completion.writeFailedDismiss")}" on:click=${this._dismissWriteAlert}>×</button>
      </div>
      <footer>KFE web · v${VERSION}</footer>
    `;
    const btn = /** @type {HTMLButtonElement | null} */ (frag.querySelector("button.back"));
    if (btn) btn.hidden = this._path === "/" || this._path === "/playlist";
    // Apply active top-nav link before the fragment is attached.
    const section = sectionForPath(this._path);
    for (const a of frag.querySelectorAll("nav.top-nav a")) {
      const el = /** @type {HTMLAnchorElement} */ (a);
      if (el.dataset.section === section) el.setAttribute("aria-current", "page");
    }
    return frag;
  }
}
